'use client';

import React, { useState, useEffect, useCallback } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';

interface ArchiveRecord {
  id: string;
  title: string;
  description: string | null;
  category: string;
  status: string;
  ai_transcription: string | null;
  corrected_transcription: string | null;
  ai_extracted_names: string[] | null;
  ai_extracted_date: string | null;
  ai_summary: string | null;
  record_date: string | null;
  created_at: string;
  tags: string[] | null;
  lodge_id: string | null;
  lodges?: { name: string; city: string | null; province_state: string | null } | null;
  user_profiles?: { full_name: string | null } | null;
}

interface Lodge {
  id: string;
  name: string;
}

const CATEGORY_LABELS: Record<string, string> = {
  lodge_records: 'Lodge Records',
  physical_artifacts: 'Physical Artifacts',
  regalia_textiles: 'Regalia & Textiles',
  member_history: 'Member History',
  building_property: 'Building & Property',
};

const STATUS_LABELS: Record<string, string> = {
  pending_review: 'Pending Review',
  approved: 'Approved',
  rejected: 'Rejected',
  archived: 'Archived',
};

const STATUS_COLORS: Record<string, { bg: string; color: string }> = {
  pending_review: { bg: 'rgba(201, 168, 76, 0.15)', color: '#7A5C1E' },
  approved: { bg: 'rgba(34, 197, 94, 0.12)', color: '#166534' },
  rejected: { bg: 'rgba(239, 68, 68, 0.12)', color: '#991b1b' },
  archived: { bg: 'rgba(99, 102, 241, 0.12)', color: '#3730a3' },
};

function highlight(text: string, query: string): React.ReactNode {
  if (!query.trim()) return text;
  const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
  const parts = text.split(regex);
  return parts.map((part, i) =>
    regex.test(part) ? (
      <mark key={i} style={{ background: 'rgba(201, 168, 76, 0.35)', color: 'inherit', borderRadius: 2, padding: '0 1px' }}>
        {part}
      </mark>
    ) : (
      part
    )
  );
}

export default function ArchiveSearch() {
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterLodge, setFilterLodge] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [results, setResults] = useState<ArchiveRecord[]>([]);
  const [lodges, setLodges] = useState<Lodge[]>([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const PER_PAGE = 12;

  // Debounce query
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query), 350);
    return () => clearTimeout(t);
  }, [query]);

  // Load lodges for filter dropdown
  useEffect(() => {
    const supabase = createClient();
    supabase
      .from('lodges')
      .select('id, name')
      .order('name')
      .then(({ data }) => {
        if (data) setLodges(data);
      });
  }, []);

  const runSearch = useCallback(async (pg: number) => {
    setLoading(true);
    setSearched(true);
    const supabase = createClient();

    const from = (pg - 1) * PER_PAGE;
    const to = from + PER_PAGE - 1;

    let q = supabase
      .from('archive_records')
      .select(
        `id, title, description, category, status, ai_transcription, corrected_transcription,
         ai_extracted_names, ai_extracted_date, ai_summary, record_date, created_at, tags, lodge_id,
         lodges(name, city, province_state),
         user_profiles!uploaded_by_user_id(full_name)`,
        { count: 'exact' }
      )
      .range(from, to)
      .order('created_at', { ascending: false });

    // Full-text search across title, transcription, date, entities
    if (debouncedQuery.trim()) {
      const term = debouncedQuery.trim();
      q = q.or(
        [
          `title.ilike.%${term}%`,
          `description.ilike.%${term}%`,
          `ai_transcription.ilike.%${term}%`,
          `corrected_transcription.ilike.%${term}%`,
          `ai_extracted_date.ilike.%${term}%`,
          `ai_summary.ilike.%${term}%`,
          `ai_extracted_names.cs.{${term}}`,
          `tags.cs.{${term}}`,
        ].join(',')
      );
    }

    if (filterCategory) q = q.eq('category', filterCategory);
    if (filterLodge) q = q.eq('lodge_id', filterLodge);
    if (filterDateFrom) q = q.gte('record_date', filterDateFrom);
    if (filterDateTo) q = q.lte('record_date', filterDateTo);

    const { data, count, error } = await q;
    setLoading(false);

    if (!error && data) {
      setResults(data as unknown as ArchiveRecord[]);
      setTotal(count ?? 0);
    } else {
      setResults([]);
      setTotal(0);
    }
  }, [debouncedQuery, filterCategory, filterLodge, filterDateFrom, filterDateTo]);

  // Auto-search when filters or debounced query change
  useEffect(() => {
    setPage(1);
    runSearch(1);
  }, [debouncedQuery, filterCategory, filterLodge, filterDateFrom, filterDateTo, runSearch]);

  const handlePageChange = (pg: number) => {
    setPage(pg);
    runSearch(pg);
  };

  const clearFilters = () => {
    setQuery('');
    setFilterCategory('');
    setFilterLodge('');
    setFilterDateFrom('');
    setFilterDateTo('');
    setPage(1);
  };

  const hasFilters = query || filterCategory || filterLodge || filterDateFrom || filterDateTo;
  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));

  const getSnippet = (record: ArchiveRecord): string => {
    let q = debouncedQuery.toLowerCase();
    const candidates = [
      record.corrected_transcription,
      record.ai_transcription,
      record.ai_summary,
      record.description,
    ].filter(Boolean) as string[];

    for (const text of candidates) {
      if (!q || text.toLowerCase().includes(q)) {
        const idx = q ? text.toLowerCase().indexOf(q) : 0;
        const start = Math.max(0, idx - 60);
        const end = Math.min(text.length, idx + 160);
        return (start > 0 ? '…' : '') + text.slice(start, end) + (end < text.length ? '…' : '');
      }
    }
    return candidates[0]?.slice(0, 200) ?? '';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-serif font-bold" style={{ color: 'var(--foreground)' }}>
          Archive Search
        </h1>
        <p className="text-sm mt-1" style={{ color: 'var(--muted-foreground)' }}>
          Search across titles, transcriptions, dates, and extracted entities
        </p>
      </div>

      {/* Search + Filters */}
      <div className="masonic-card p-4 space-y-4">
        {/* Main search bar */}
        <div className="relative">
          <Icon
            name="MagnifyingGlassIcon"
            size={18}
            className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
            style={{ color: 'var(--muted-foreground)' } as React.CSSProperties}
          />
          {loading && (
            <div
              className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 rounded-full border-2 border-t-transparent animate-spin"
              style={{ borderColor: 'var(--accent)', borderTopColor: 'transparent' }}
            />
          )}
          <input
            className="masonic-input pl-10 pr-10 py-3 text-base w-full"
            placeholder="Search by title, transcription, date, names, entities…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoFocus
          />
        </div>

        {/* Filter row */}
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex flex-col gap-1 min-w-44">
            <label className="text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--muted-foreground)' }}>
              Category
            </label>
            <select
              className="masonic-select text-sm py-2 h-10"
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
            >
              <option value="">All Categories</option>
              {Object.entries(CATEGORY_LABELS).map(([val, label]) => (
                <option key={val} value={val}>{label}</option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-1 min-w-44">
            <label className="text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--muted-foreground)' }}>
              Lodge
            </label>
            <select
              className="masonic-select text-sm py-2 h-10"
              value={filterLodge}
              onChange={(e) => setFilterLodge(e.target.value)}
            >
              <option value="">All Lodges</option>
              {lodges.map((l) => (
                <option key={l.id} value={l.id}>{l.name}</option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--muted-foreground)' }}>
              Date From
            </label>
            <input
              type="date"
              className="masonic-input text-sm py-2 h-10 w-40"
              value={filterDateFrom}
              onChange={(e) => setFilterDateFrom(e.target.value)}
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--muted-foreground)' }}>
              Date To
            </label>
            <input
              type="date"
              className="masonic-input text-sm py-2 h-10 w-40"
              value={filterDateTo}
              onChange={(e) => setFilterDateTo(e.target.value)}
            />
          </div>

          {hasFilters && (
            <button
              className="masonic-btn-outline text-sm h-10 px-3 self-end"
              onClick={clearFilters}
            >
              <Icon name="XMarkIcon" size={15} />
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Results summary */}
      {searched && (
        <div className="flex items-center justify-between">
          <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
            {loading ? 'Searching…' : (
              <>
                <span className="font-semibold" style={{ color: 'var(--foreground)' }}>{total}</span>
                {' '}record{total !== 1 ? 's' : ''} found
                {debouncedQuery && (
                  <> for <span className="font-semibold" style={{ color: 'var(--accent)' }}>"{debouncedQuery}"</span></>
                )}
              </>
            )}
          </p>
          {totalPages > 1 && (
            <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
              Page {page} of {totalPages}
            </p>
          )}
        </div>
      )}

      {/* Results list */}
      {!loading && searched && results.length === 0 && (
        <div
          className="masonic-card p-12 flex flex-col items-center justify-center text-center"
          style={{ color: 'var(--muted-foreground)' }}
        >
          <Icon name="MagnifyingGlassIcon" size={40} className="mb-4 opacity-30" />
          <p className="text-lg font-semibold mb-1" style={{ color: 'var(--foreground)' }}>No records found</p>
          <p className="text-sm">Try adjusting your search terms or filters</p>
        </div>
      )}

      {!loading && !searched && (
        <div
          className="masonic-card p-12 flex flex-col items-center justify-center text-center"
          style={{ color: 'var(--muted-foreground)' }}
        >
          <Icon name="BookOpenIcon" size={40} className="mb-4 opacity-30" />
          <p className="text-lg font-semibold mb-1" style={{ color: 'var(--foreground)' }}>Search the Archive</p>
          <p className="text-sm">Enter a search term or apply filters to find records</p>
        </div>
      )}

      <div className="space-y-3">
        {results.map((record) => {
          const snippet = getSnippet(record);
          const statusStyle = STATUS_COLORS[record.status] ?? { bg: 'var(--muted)', color: 'var(--muted-foreground)' };
          const lodge = record.lodges as any;
          const uploader = record.user_profiles as any;

          return (
            <div
              key={record.id}
              className="masonic-card p-5 hover:shadow-masonic-md transition-shadow"
              style={{ borderLeft: '3px solid var(--accent)' }}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  {/* Title */}
                  <div className="flex items-center gap-2 flex-wrap mb-2">
                    <h3 className="text-base font-semibold font-serif" style={{ color: 'var(--foreground)' }}>
                      {highlight(record.title, debouncedQuery)}
                    </h3>
                    <span
                      className="text-xs font-medium px-2 py-0.5 rounded-full"
                      style={{ background: statusStyle.bg, color: statusStyle.color }}
                    >
                      {STATUS_LABELS[record.status] ?? record.status}
                    </span>
                    {record.category && (
                      <span
                        className="text-xs px-2 py-0.5 rounded-full"
                        style={{ background: 'var(--muted)', color: 'var(--muted-foreground)' }}
                      >
                        {CATEGORY_LABELS[record.category] ?? record.category}
                      </span>
                    )}
                  </div>

                  {/* Meta row */}
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs mb-3" style={{ color: 'var(--muted-foreground)' }}>
                    {(record.ai_extracted_date || record.record_date) && (
                      <span className="flex items-center gap-1">
                        <Icon name="CalendarIcon" size={12} />
                        {highlight(record.ai_extracted_date ?? record.record_date ?? '', debouncedQuery)}
                      </span>
                    )}
                    {lodge?.name && (
                      <span className="flex items-center gap-1">
                        <Icon name="BuildingLibraryIcon" size={12} />
                        {lodge.name}
                        {lodge.city && `, ${lodge.city}`}
                      </span>
                    )}
                    {uploader?.full_name && (
                      <span className="flex items-center gap-1">
                        <Icon name="UserIcon" size={12} />
                        {uploader.full_name}
                      </span>
                    )}
                  </div>

                  {/* Transcription snippet */}
                  {snippet && (
                    <p className="text-sm leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
                      {highlight(snippet, debouncedQuery)}
                    </p>
                  )}

                  {/* Extracted entities */}
                  {record.ai_extracted_names && record.ai_extracted_names.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-3">
                      {record.ai_extracted_names.slice(0, 6).map((name, i) => (
                        <span
                          key={i}
                          className="text-xs px-2 py-0.5 rounded-full"
                          style={{ background: 'rgba(201, 168, 76, 0.12)', color: 'var(--accent)' }}
                        >
                          {highlight(name, debouncedQuery)}
                        </span>
                      ))}
                      {record.ai_extracted_names.length > 6 && (
                        <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
                          +{record.ai_extracted_names.length - 6} more
                        </span>
                      )}
                    </div>
                  )}

                  {/* Tags */}
                  {record.tags && record.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {record.tags.slice(0, 5).map((tag, i) => (
                        <span
                          key={i}
                          className="text-xs px-1.5 py-0.5 rounded"
                          style={{ background: 'var(--muted)', color: 'var(--muted-foreground)' }}
                        >
                          #{tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Action */}
                <Link
                  href={`/document-transcription-review?id=${record.id}`}
                  className="masonic-btn-outline text-xs px-3 py-2 flex-shrink-0 flex items-center gap-1"
                >
                  <Icon name="EyeIcon" size={13} />
                  View
                </Link>
              </div>
            </div>
          );
        })}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 pt-2">
          <button
            className="masonic-btn-outline text-sm px-3 py-2"
            disabled={page === 1}
            onClick={() => handlePageChange(page - 1)}
          >
            <Icon name="ChevronLeftIcon" size={15} />
          </button>
          {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
            let pg = i + 1;
            if (totalPages > 7) {
              if (page <= 4) pg = i + 1;
              else if (page >= totalPages - 3) pg = totalPages - 6 + i;
              else pg = page - 3 + i;
            }
            return (
              <button
                key={pg}
                className={`text-sm px-3 py-2 rounded-lg font-medium transition-colors ${page === pg ? 'masonic-btn-primary' : 'masonic-btn-outline'}`}
                onClick={() => handlePageChange(pg)}
              >
                {pg}
              </button>
            );
          })}
          <button
            className="masonic-btn-outline text-sm px-3 py-2"
            disabled={page === totalPages}
            onClick={() => handlePageChange(page + 1)}
          >
            <Icon name="ChevronRightIcon" size={15} />
          </button>
        </div>
      )}
    </div>
  );
}
